import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Power, RefreshCw, Loader, TrendingUp, TrendingDown, Copy, ExternalLink, Globe, MessageCircle, Check, Settings, DollarSign, Share2, Download, Eye, EyeOff, X, Upload, Image } from 'lucide-react';
import GlobalDropdown from '../../components/GlobalDropdown';
import { useWalletUser } from '../../hooks/useWalletUser';
import { getUserTradingWallets, TradingWallet } from '../../services/tradingWalletService';
import { getUserProfile, updateActiveTradingWallet, notifyActiveWalletChanged, getActivePositionsQuickSellPreferences, updateActivePositionsQuickSellPreferences, ActivePositionsQuickSellPreferences } from '../../services/userProfileService';
import { sniperPositionsService, SniperPosition } from '../../services/sniperPositionsService';
import { getBalance, getTokenBalances } from '../../services/shyftService';
import { restoreTradingWalletKeypair } from '../../services/tradingWalletService';
import { jupiterService } from '../../services/jupiterService';
import { getWalletTokensWithMetadata, getTokenInfo } from '../../services/solanatrackerService';
import { wsService } from '../../services/websocketService';
import { Token } from '../../types/supabase';

// console.log = () => {};
// console.warn = () => {};
// console.error = () => {};

interface ActivePositionsProps {
  onClose?: () => void;
  isWidget?: boolean;
}

interface TokenBalance {
  mint: string;
  symbol: string | null;
  name: string | null;
  balance: string;
  value?: number;
  image?: string | null;
  socials?: {
    twitter?: string;
    telegram?: string;
    discord?: string;
    website?: string;
  };
}

interface TokenPnL {
  holding: number;
  held: number;
  sold: number;
  realized: number;
  unrealized: number;
  total: number;
  total_sold: number;
  total_invested: number;
  average_buy_amount: number;
  current_value: number;
  cost_basis: number;
}

interface WalletPnL {
  tokens: {
    [key: string]: TokenPnL;
  };
  summary: {
    realized: number;
    unrealized: number;
    total: number;
    totalInvested: number;
    averageBuyAmount: number;
    totalWins: number;
    totalLosses: number;
    winPercentage: number;
    lossPercentage: number;
  };
}

interface WalletOption {
  address: string;
  name: string;
  type: 'main' | 'trading';
  isActive?: boolean;
  balance?: number;
}

interface TokenMetadata {
  mint: string;
  symbol: string;
  name: string;
  image: string | null;
  decimals: number;
}

interface ShareData {
  type: 'token' | 'wallet';
  tokenMint?: string;
  tokenSymbol?: string;
  tokenName?: string;
  tokenImage?: string | null;
  pnl: number;
  roi?: number;
  invested?: number;
  currentValue?: number;
  walletAddress?: string;
  walletName?: string;
  totalTokens?: number;
  winRate?: number;
}

interface ShareCustomization {
  customText: string;
  showPrimaryWallet: boolean;
  showTradingWallet: boolean;
  showExactAmounts: boolean;
  showROI: boolean;
  showInvested: boolean;
  showCurrentValue: boolean;
  showTokenCount: boolean;
  showWinRate: boolean;
  showTokenImage: boolean;
  useTokenAsBackground: boolean;
  tokenBackgroundOpacity: number;
  currencyDisplay: 'SOL' | 'USD';
  backgroundStyle: 'gradient' | 'solid' | 'pattern' | 'custom' | 'token';
  colorScheme: 'green' | 'blue' | 'purple' | 'gold';
  customBackgroundImage: string | null;
}

// X (Twitter) Logo Component
const XLogo: React.FC<{ size?: number; className?: string }> = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const ActivePositions: React.FC<ActivePositionsProps> = ({ onClose }) => {
  const walletUser = useWalletUser();
  const [tradingWallets, setTradingWallets] = useState<TradingWallet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeWalletKey, setActiveWalletKey] = useState<string | null>(
    localStorage.getItem('activeWalletKey')
  );
  const [currentViewingWallet, setCurrentViewingWallet] = useState<string | null>(null);
  const [availableWallets, setAvailableWallets] = useState<WalletOption[]>([]);
  const [isSynchronizing, setIsSynchronizing] = useState(false);
  const [walletBalances, setWalletBalances] = useState<{[key: string]: number}>({});
  const [tokenBalances, setTokenBalances] = useState<{[key: string]: TokenBalance[]}>({});
  const [walletPnL, setWalletPnL] = useState<{[key: string]: WalletPnL}>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [isLoadingPnL, setIsLoadingPnL] = useState(false);

  // Token metadata state
  const [tokenMetadata, setTokenMetadata] = useState<{[key: string]: TokenMetadata}>({});
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  
  // Cache for metadata to avoid refetching when switching wallets
  const [metadataCache] = useState<Map<string, {metadata: TokenMetadata, timestamp: number}>>(new Map());

  // WebSocket subscriptions and real-time prices
  const [activeSubscriptions, setActiveSubscriptions] = useState<Map<string, () => void>>(new Map());
  const [realtimePrices, setRealtimePrices] = useState<{[tokenMint: string]: {priceUsd: number, lastUpdated: number}}>({});
  
  // Ref to track loading state and prevent duplicate calls
  const isLoadingRef = React.useRef(false);

  // Quick sell feature state
  const [quickSellPreferences, setQuickSellPreferences] = useState<ActivePositionsQuickSellPreferences>({
    sellPercentage: 25,
    confirmBeforeSell: true,
    slippagePercent: 5,
    enabled: true
  });
  const [showQuickSellSettings, setShowQuickSellSettings] = useState(false);
  const [confirmSells, setConfirmSells] = useState<{[tokenMint: string]: boolean}>({});
  const [sellLoading, setSellLoading] = useState<{[tokenMint: string]: boolean}>({});

  // Sharing functionality state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [shareCustomization, setShareCustomization] = useState<ShareCustomization>({
    customText: '',
    showPrimaryWallet: false,
    showTradingWallet: false,
    showExactAmounts: true,
    showROI: true,
    showInvested: true,
    showCurrentValue: true,
    showTokenCount: true,
    showWinRate: true,
    showTokenImage: true,
    useTokenAsBackground: true,
    tokenBackgroundOpacity: 15,
    currencyDisplay: 'SOL',
    backgroundStyle: 'token',
    colorScheme: 'green',
    customBackgroundImage: null
  });
  const [isGeneratingCard, setIsGeneratingCard] = useState(false);
  const [generatedCardUrl, setGeneratedCardUrl] = useState<string | null>(null);

  // Hide/Show tokens state
  const [hiddenTokens, setHiddenTokens] = useState<Set<string>>(new Set());
  const [showingHidden, setShowingHidden] = useState<boolean>(false);

  // Sniper positions state
  const [sniperPositions, setSniperPositions] = useState<SniperPosition[]>([]);
  const [isLoadingSniperPositions, setIsLoadingSniperPositions] = useState(false);

  // Fetch sniper positions for a wallet
  const fetchSniperPositions = useCallback(async (walletAddress: string) => {
    if (!walletAddress) return;
    
    setIsLoadingSniperPositions(true);
    
    try {
      const positions = await sniperPositionsService.getUserSniperPositions(walletAddress);
      setSniperPositions(positions);
      console.log(`📊 Loaded ${positions.length} sniper positions for wallet ${walletAddress.slice(0, 8)}...`);
    } catch (error) {
      console.error('Error fetching sniper positions:', error);
      setSniperPositions([]);
    } finally {
      setIsLoadingSniperPositions(false);
    }
  }, []);

  // Fetch PnL data for a wallet
  const fetchWalletPnL = useCallback(async (walletAddress: string) => {
    if (!walletAddress || isLoadingRef.current) return;
    
    isLoadingRef.current = true;
    setIsLoadingPnL(true);
    
    try {
      console.log(`🔄 Fetching PnL data for wallet: ${walletAddress.slice(0, 8)}...`);
      
      const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
      if (!apiKey) {
        throw new Error('VITE_SOLANA_TRACKER_API_KEY not found in environment variables');
      }
      
      const response = await fetch(`https://data.solanatracker.io/pnl/${walletAddress}`, {
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`PnL API error: ${response.status} ${response.statusText}`);
      }
      
      const pnlData = await response.json();
      console.log(`✅ PnL data received for wallet ${walletAddress.slice(0, 8)}...`, {
        tokensCount: Object.keys(pnlData.tokens || {}).length,
        tokensWithHoldings: Object.entries(pnlData.tokens || {}).filter(([_, token]) => (token as any).holding > 0).length,
        summary: pnlData.summary
      });
      
      // Update the walletPnL state with the received data
      setWalletPnL(prev => ({
        ...prev,
        [walletAddress]: pnlData
      }));
      
      // Fetch token metadata for all tokens with holdings (async - don't wait)
      const tokensWithHoldings = Object.entries(pnlData.tokens || {})
        .filter(([_, tokenPnL]) => (tokenPnL as any).holding > 0)
        .map(([mint, _]) => mint);
      
      if (tokensWithHoldings.length > 0) {
        // Run metadata fetching in parallel to improve perceived performance
        fetchTokenMetadata(walletAddress, tokensWithHoldings).catch(err => {
          console.error('Failed to fetch token metadata:', err);
        });
        
        // Subscribe to real-time price updates for these tokens
        subscribeToTokenPrices(tokensWithHoldings).catch(err => {
          console.error('Failed to subscribe to token prices:', err);
        });
      }
      
    } catch (error) {
      console.error('❌ Error fetching PnL data:', error);
      setError(`Failed to load PnL data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingPnL(false);
      isLoadingRef.current = false;
    }
  }, []); // Keep empty dependency array to prevent re-creation

  // Fetch token metadata for tokens
  const fetchTokenMetadata = useCallback(async (walletAddress: string, tokenMints: string[]) => {
    if (!walletAddress || !tokenMints.length) return;
    
    setIsLoadingMetadata(true);
    console.log(`🏷️ Fetching metadata for ${tokenMints.length} tokens from wallet ${walletAddress.slice(0, 8)}...`);
    
    // Check cache first and apply any cached metadata immediately
    const now = Date.now();
    const cacheTimeout = 5 * 60 * 1000; // 5 minutes
    const metadataMap: {[key: string]: TokenMetadata} = {};
    const uncachedTokens: string[] = [];
    
    tokenMints.forEach(mint => {
      const cached = metadataCache.get(mint);
      if (cached && (now - cached.timestamp) < cacheTimeout) {
        metadataMap[mint] = cached.metadata;
      } else {
        uncachedTokens.push(mint);
      }
    });
    
    // Apply cached metadata immediately
    if (Object.keys(metadataMap).length > 0) {
      console.log(`📋 Applied ${Object.keys(metadataMap).length} cached token metadata`);
      setTokenMetadata(prev => ({
        ...prev,
        ...metadataMap
      }));
    }
    
    // If all tokens were cached, we're done
    if (uncachedTokens.length === 0) {
      setIsLoadingMetadata(false);
      return;
    }
    
    console.log(`🔄 Fetching metadata for ${uncachedTokens.length} uncached tokens...`);
    
    try {
              // Strategy 1: Use the wallet API first (most efficient - gets all tokens in 1 call)
        console.log(`📡 Trying wallet API for bulk metadata...`);
        const walletResponse = await getWalletTokensWithMetadata(walletAddress);
        
        if (walletResponse.status === 'success' && walletResponse.data?.tokens) {
          console.log(`✅ Wallet API returned ${walletResponse.data.tokens.length} tokens`);
          
          // Process tokens from wallet API
          walletResponse.data.tokens.forEach((token: any) => {
            const metadata: TokenMetadata = {
              mint: token.mint,
              symbol: token.symbol || 'Unknown',
              name: token.name || 'Unknown Token',
              image: token.image || null,
              decimals: token.decimals || 9
            };
            
            // Only add if this token is in our uncached list
            if (uncachedTokens.includes(token.mint)) {
              metadataMap[token.mint] = metadata;
              // Cache the metadata
              metadataCache.set(token.mint, {
                metadata,
                timestamp: now
              });
            }
          });
          
          // Check which tokens we still need
          const missingTokens = uncachedTokens.filter(mint => !metadataMap[mint]);
        
        if (missingTokens.length > 0) {
          console.log(`🔍 ${missingTokens.length} tokens missing from wallet API, trying batch endpoint...`);
          
          // Strategy 2: Try batch tokens endpoint for missing tokens
          try {
            const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
            const batchResponse = await fetch(`https://data.solanatracker.io/tokens/multiple?tokens=${missingTokens.join(',')}`, {
              method: 'GET',
              headers: {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
              }
            });
            
            if (batchResponse.ok) {
              const batchData = await batchResponse.json();
              console.log(`✅ Batch API returned data for missing tokens`);
              
              // Process batch response
              if (Array.isArray(batchData)) {
                                 batchData.forEach((tokenData: any) => {
                   if (tokenData.token) {
                     const token = tokenData.token;
                     const metadata: TokenMetadata = {
                       mint: token.mint,
                       symbol: token.symbol || 'Unknown',
                       name: token.name || 'Unknown Token',
                       image: token.image || null,
                       decimals: token.decimals || 9
                     };
                     metadataMap[token.mint] = metadata;
                     // Cache the metadata
                     metadataCache.set(token.mint, {
                       metadata,
                       timestamp: now
                     });
                   }
                 });
              }
              
              // Update missing tokens list
              const stillMissingTokens = missingTokens.filter(mint => !metadataMap[mint]);
              
              // Strategy 3: Individual calls for remaining tokens (with rate limiting)
              if (stillMissingTokens.length > 0 && stillMissingTokens.length <= 5) {
                console.log(`🔄 Fetching ${stillMissingTokens.length} tokens individually with rate limiting...`);
                
                const individualPromises = stillMissingTokens.map(async (mint, index) => {
                  // Add delay to avoid rate limiting (100ms between requests)
                  await new Promise(resolve => setTimeout(resolve, index * 100));
                  
                  try {
                    const tokenResponse = await getTokenInfo(mint);
                    if (tokenResponse.status === 'success' && tokenResponse.data?.token) {
                      const token = tokenResponse.data.token;
                      metadataMap[mint] = {
                        mint,
                        symbol: token.symbol || 'Unknown',
                        name: token.name || 'Unknown Token',
                        image: token.image || null,
                        decimals: token.decimals || 9
                      };
                    } else {
                      // Fallback for tokens we can't fetch
                      metadataMap[mint] = {
                        mint,
                        symbol: mint.slice(0, 6),
                        name: 'Unknown Token',
                        image: null,
                        decimals: 9
                      };
                    }
                  } catch (error) {
                    console.warn(`Failed to fetch metadata for token ${mint}:`, error);
                    metadataMap[mint] = {
                      mint,
                      symbol: mint.slice(0, 6),
                      name: 'Unknown Token',
                      image: null,
                      decimals: 9
                    };
                  }
                });
                
                await Promise.all(individualPromises);
              } else if (stillMissingTokens.length > 5) {
                // Too many missing tokens, create fallbacks
                console.log(`⚠️ Too many missing tokens (${stillMissingTokens.length}), creating fallbacks`);
                stillMissingTokens.forEach(mint => {
                  metadataMap[mint] = {
                    mint,
                    symbol: mint.slice(0, 6),
                    name: 'Unknown Token',
                    image: null,
                    decimals: 9
                  };
                });
              }
            } else {
              console.warn(`⚠️ Batch endpoint failed, falling back to individual calls`);
              // Fall back to individual calls with rate limiting for missing tokens
              await fetchMissingTokensIndividually(missingTokens, metadataMap);
            }
          } catch (batchError) {
            console.warn(`⚠️ Batch endpoint error:`, batchError);
            // Fall back to individual calls for missing tokens
            await fetchMissingTokensIndividually(missingTokens, metadataMap);
          }
        }
        
        // Update token metadata state
        setTokenMetadata(prev => ({
          ...prev,
          ...metadataMap
        }));
        
        console.log(`✅ Token metadata updated for ${Object.keys(metadataMap).length}/${uncachedTokens.length} new tokens (${tokenMints.length} total)`);
        
      } else {
        console.warn('⚠️ Wallet API failed, using fallback approach');
        await createFallbackMetadata(uncachedTokens);
      }
      
    } catch (error) {
      console.error('❌ Error fetching token metadata:', error);
      await createFallbackMetadata(uncachedTokens);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, []);

  // Helper function to fetch missing tokens individually with rate limiting
  const fetchMissingTokensIndividually = async (missingTokens: string[], metadataMap: {[key: string]: TokenMetadata}) => {
    if (missingTokens.length > 10) {
      console.log(`⚠️ Too many missing tokens (${missingTokens.length}), skipping individual fetches`);
      missingTokens.forEach(mint => {
        metadataMap[mint] = {
          mint,
          symbol: mint.slice(0, 6),
          name: 'Unknown Token',
          image: null,
          decimals: 9
        };
      });
      return;
    }
    
    const individualPromises = missingTokens.map(async (mint, index) => {
      // Rate limiting: 100ms delay between requests
      await new Promise(resolve => setTimeout(resolve, index * 100));
      
      try {
        const tokenResponse = await getTokenInfo(mint);
        if (tokenResponse.status === 'success' && tokenResponse.data?.token) {
          const token = tokenResponse.data.token;
          metadataMap[mint] = {
            mint,
            symbol: token.symbol || 'Unknown',
            name: token.name || 'Unknown Token',
            image: token.image || null,
            decimals: token.decimals || 9
          };
        } else {
          metadataMap[mint] = {
            mint,
            symbol: mint.slice(0, 6),
            name: 'Unknown Token',
            image: null,
            decimals: 9
          };
        }
      } catch (error) {
        console.warn(`Failed to fetch metadata for token ${mint}:`, error);
        metadataMap[mint] = {
          mint,
          symbol: mint.slice(0, 6),
          name: 'Unknown Token',
          image: null,
          decimals: 9
        };
      }
    });
    
    await Promise.all(individualPromises);
  };

  // Helper function to create fallback metadata
  const createFallbackMetadata = async (tokenMints: string[]) => {
    const fallbackMetadata: {[key: string]: TokenMetadata} = {};
    tokenMints.forEach(mint => {
      fallbackMetadata[mint] = {
        mint,
        symbol: mint.slice(0, 6),
        name: 'Unknown Token',
        image: null,
        decimals: 9
      };
    });
    
    setTokenMetadata(prev => ({
      ...prev,
      ...fallbackMetadata
    }));
  };

  // WebSocket subscription management
  const subscribeToTokenPrices = useCallback(async (tokenMints: string[]) => {
    if (!tokenMints.length) return;
    
    console.log(`🔔 Subscribing to ${tokenMints.length} token price feeds:`, 
      tokenMints.map(mint => mint.slice(0, 8) + '...')
    );
    
    // Clear previous subscriptions to avoid duplicates
    unsubscribeFromAllTokens();
    
    const newSubscriptions = new Map<string, () => void>();
    
    tokenMints.forEach(tokenMint => {
      try {
        // Subscribe to token room for real-time updates
        const roomId = `token:${tokenMint}`;
        console.log(`🔔 Attempting to subscribe to room: ${roomId}`);
        wsService.joinRoom(roomId);
        
        // Set up event listener for this token
        const unsubscribe = wsService.on(roomId, (data: any) => {
          if (data && data.price && data.price.usd) {
            console.log(`💰 Price update for ${tokenMint.slice(0, 8)}...: $${data.price.usd}`);
            
            setRealtimePrices(prev => ({
              ...prev,
              [tokenMint]: {
                priceUsd: data.price.usd,
                lastUpdated: data.lastUpdated || Date.now()
              }
            }));
          } else {
            console.warn(`⚠️ Invalid price data received for ${tokenMint.slice(0, 8)}...`, data);
          }
        });
        
        newSubscriptions.set(tokenMint, () => {
          unsubscribe();
          wsService.leaveRoom(roomId);
        });
        
        console.log(`✅ Successfully subscribed to ${tokenMint.slice(0, 8)}...`);
        
      } catch (error) {
        console.error(`❌ Failed to subscribe to token ${tokenMint.slice(0, 8)}...:`, error);
      }
    });
    
    // Update active subscriptions
    setActiveSubscriptions(prev => {
      const updated = new Map(prev);
      newSubscriptions.forEach((unsubscribe, tokenMint) => {
        updated.set(tokenMint, unsubscribe);
      });
      return updated;
    });
    
    console.log(`✅ Total subscriptions: ${newSubscriptions.size}/${tokenMints.length} successful`);
    
    // Add debugging info for tokens without real-time data after a delay
    setTimeout(() => {
      const tokensWithoutRealtime = tokenMints.filter(mint => !realtimePrices[mint]);
      if (tokensWithoutRealtime.length > 0) {
        console.warn(`⚠️ Tokens not receiving real-time data (${tokensWithoutRealtime.length}):`, 
          tokensWithoutRealtime.map(mint => mint.slice(0, 8) + '...')
        );
        console.log('💡 Possible reasons:');
        console.log('  - Token not supported by WebSocket feed');
        console.log('  - Room does not exist on server');
        console.log('  - Connection issues');
        console.log('  - Very new/inactive tokens');
      }
    }, 5000); // Check after 5 seconds
  }, []); // Remove activeSubscriptions dependency since we clear all before subscribing

  const unsubscribeFromTokens = useCallback((tokenMints: string[]) => {
    if (!tokenMints.length) return;
    
    setActiveSubscriptions(prevSubs => {
      console.log(`🔌 Unsubscribing from ${tokenMints.length} specific tokens...`);
      
      const updated = new Map(prevSubs);
      let unsubscribedCount = 0;
      
      tokenMints.forEach(tokenMint => {
        const unsubscribe = updated.get(tokenMint);
        if (unsubscribe) {
          try {
            unsubscribe();
            updated.delete(tokenMint);
            unsubscribedCount++;
          } catch (error) {
            console.error(`❌ Error unsubscribing from ${tokenMint}:`, error);
          }
        }
      });
      
      console.log(`✅ Unsubscribed from ${unsubscribedCount} tokens, ${updated.size} remaining`);
      return updated;
    });
    
    // Remove real-time prices for unsubscribed tokens
    setRealtimePrices(prev => {
      const updated = { ...prev };
      tokenMints.forEach(tokenMint => {
        delete updated[tokenMint];
      });
      return updated;
    });
  }, []);

  const unsubscribeFromAllTokens = useCallback(() => {
    setActiveSubscriptions(prevSubs => {
      console.log(`🔌 Unsubscribing from ${prevSubs.size} token price feeds...`);
      
      prevSubs.forEach((unsubscribe, tokenMint) => {
        try {
          unsubscribe();
        } catch (error) {
          console.error(`❌ Error unsubscribing from ${tokenMint}:`, error);
        }
      });
      
      console.log(`✅ Unsubscribed from all token price feeds`);
      return new Map();
    });
    
    setRealtimePrices({});
  }, []); // Remove activeSubscriptions dependency to prevent infinite loop

  const calculateRealTimePnL = useCallback((tokenMint: string, tokenPnL: any) => {
    const realtimePrice = realtimePrices[tokenMint];
    if (!realtimePrice) {
      // No real-time price, use original values
      return {
        currentValue: tokenPnL.current_value,
        unrealized: tokenPnL.unrealized,
        total: tokenPnL.total
      };
    }
    
    // Calculate new values based on real-time price
    const holding = tokenPnL.holding;
    const newCurrentValue = holding * realtimePrice.priceUsd;
    
    // Calculate unrealized P&L correctly:
    // For current holdings, we need to compare current value vs cost basis of those holdings
    // cost_basis represents the average price paid per token
    // So the cost of current holdings = holding * cost_basis
    const costOfCurrentHoldings = holding * tokenPnL.cost_basis;
    const newUnrealized = newCurrentValue - costOfCurrentHoldings;
    
    // Total P&L = realized (from past sales) + unrealized (from current holdings)
    const newTotal = tokenPnL.realized + newUnrealized;
    
    return {
      currentValue: newCurrentValue,
      unrealized: newUnrealized,
      total: newTotal,
      isRealTime: true
    };
  }, [realtimePrices]);

  // Calculate real-time summary P&L for the entire wallet - memoized
  const calculateRealTimeSummary = useCallback((walletAddress: string) => {
    if (!walletPnL[walletAddress]) {
      return null;
    }
    
    const originalSummary = walletPnL[walletAddress].summary;
    let totalUnrealized = 0;
    let totalCurrent = 0;
    let hasAnyRealtimeData = false;
    
    // Calculate new totals based on real-time prices
    Object.entries(walletPnL[walletAddress].tokens).forEach(([tokenMint, tokenData]) => {
      if (tokenData.holding > 0) {
        const realtimePrice = realtimePrices[tokenMint];
        if (realtimePrice) {
          // Use real-time calculation with corrected P&L logic
          const newCurrentValue = tokenData.holding * realtimePrice.priceUsd;
          const costOfCurrentHoldings = tokenData.holding * tokenData.cost_basis;
          const newUnrealized = newCurrentValue - costOfCurrentHoldings;
          totalUnrealized += newUnrealized;
          totalCurrent += newCurrentValue;
          hasAnyRealtimeData = true;
        } else {
          // Use original values
          totalUnrealized += tokenData.unrealized;
          totalCurrent += tokenData.current_value;
        }
      }
    });
    
    const newTotal = originalSummary.realized + totalUnrealized;
    
    return {
      ...originalSummary,
      unrealized: totalUnrealized,
      total: newTotal,
      currentValue: totalCurrent,
      hasRealtimeData: hasAnyRealtimeData
    };
  }, [walletPnL, realtimePrices]);

  // Load user data and trading wallets
  useEffect(() => {
    const loadData = async () => {
      if (!walletUser.walletAddress) return;
      
      try {
        setIsLoading(true);
        
        // Get user profile to check active trading wallet
        const profile = await getUserProfile(walletUser.walletAddress);
        
        // Prioritize localStorage activeWalletKey (what WalletConnection uses) over profile
        const storedActiveWallet = localStorage.getItem('activeWalletKey');
        const activeWallet = storedActiveWallet || profile?.active_trade_wallet;
        
        if (activeWallet) {
          setActiveWalletKey(activeWallet);
        }
        
        // Load trading wallets
        const wallets = await getUserTradingWallets(walletUser.walletAddress);
        setTradingWallets(wallets);
        
        // Load balances for all wallets (main + trading wallets)
        const balances = new Map<string, number>();
        const allWalletAddresses = [walletUser.walletAddress, ...wallets.map(w => w.public_key)];
        
        // Load balances in parallel
        const balancePromises = allWalletAddresses.map(async (address) => {
          try {
            const balance = await getBalance(address);
            return { address, balance: balance / 1_000_000_000 }; // Convert to SOL
          } catch (error) {
            console.error(`Error loading balance for wallet ${address.slice(0, 8)}...`, error);
            return { address, balance: 0 };
          }
        });
        
        const balanceResults = await Promise.all(balancePromises);
        balanceResults.forEach(({ address, balance }) => {
          balances.set(address, balance);
        });
        
        setWalletBalances(Object.fromEntries(balances));
        
        // Build available wallets list with balances (only trading wallets)
        const walletOptions: WalletOption[] = wallets.map(wallet => ({
          address: wallet.public_key,
          name: wallet.name,
          type: 'trading' as const,
          isActive: wallet.public_key === profile?.active_trade_wallet,
          balance: balances.get(wallet.public_key) || 0
        })).sort((a, b) => {
          // First sort by whether they have balance (wallets with balance come first)
          const aHasBalance = a.balance > 0;
          const bHasBalance = b.balance > 0;
          
          if (aHasBalance && !bHasBalance) return -1;
          if (!aHasBalance && bHasBalance) return 1;
          
          // If both have balance or both don't, sort by balance amount (highest first)
          return b.balance - a.balance;
        });
        
        setAvailableWallets(walletOptions);
        
        // Set default viewing wallet (prioritize currently active trading wallet from localStorage, then profile, then first trading wallet)
        const currentActiveWallet = localStorage.getItem('activeWalletKey');
        const firstTradingWallet = walletOptions.length > 0 ? walletOptions[0].address : null;
        const defaultWallet = currentActiveWallet || profile?.active_trade_wallet || firstTradingWallet;
        
        console.log(`📊 ActivePositions: Setting default viewing wallet:`, {
          storedActiveWallet: currentActiveWallet?.slice(0, 8) + '...' || 'none',
          profileActiveWallet: profile?.active_trade_wallet?.slice(0, 8) + '...' || 'none',
          firstTradingWallet: firstTradingWallet?.slice(0, 8) + '...' || 'none',
          selectedDefault: defaultWallet?.slice(0, 8) + '...' || 'none'
        });
        
        if (defaultWallet) {
        setCurrentViewingWallet(defaultWallet);
          
          // Fetch PnL data and sniper positions for the default wallet
          await Promise.all([
            fetchWalletPnL(defaultWallet),
            fetchSniperPositions(defaultWallet)
          ]);
        }
        
      } catch (err) {
        console.error('Error loading trading wallets:', err);
        setError('Failed to load trading wallets');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [walletUser.walletAddress]); // Removed fetchWalletPnL dependency to prevent infinite loop

  // Fetch PnL data when viewing wallet changes
  useEffect(() => {
    if (currentViewingWallet && !isLoading && !isLoadingRef.current) {
      console.log(`🔄 Viewing wallet changed to: ${currentViewingWallet.slice(0, 8)}... - Fetching PnL data`);
      
      // Immediately unsubscribe from all current tokens when switching wallets
      unsubscribeFromAllTokens();
      
      // Call fetchWalletPnL directly since it has loading protection
      const fetchData = async () => {
        if (!isLoadingRef.current) {
          isLoadingRef.current = true;
          setIsLoadingPnL(true);
          
          try {
            console.log(`🔄 Fetching PnL data for wallet: ${currentViewingWallet.slice(0, 8)}...`);
            
            const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
            if (!apiKey) {
              throw new Error('VITE_SOLANA_TRACKER_API_KEY not found in environment variables');
            }
            
            const response = await fetch(`https://data.solanatracker.io/pnl/${currentViewingWallet}`, {
              method: 'GET',
              headers: {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
              }
            });
            
            if (!response.ok) {
              throw new Error(`PnL API error: ${response.status} ${response.statusText}`);
            }
            
            const pnlData = await response.json();
            console.log(`✅ PnL data received for wallet ${currentViewingWallet.slice(0, 8)}...`, {
              tokensCount: Object.keys(pnlData.tokens || {}).length,
              tokensWithHoldings: Object.entries(pnlData.tokens || {}).filter(([_, token]) => (token as any).holding > 0).length,
              summary: pnlData.summary
            });
            
            // Update the walletPnL state with the received data
            setWalletPnL(prev => ({
              ...prev,
              [currentViewingWallet]: pnlData
            }));
            
            // Fetch token metadata for all tokens with holdings
            const tokensWithHoldings = Object.entries(pnlData.tokens || {})
              .filter(([_, tokenPnL]) => (tokenPnL as any).holding > 0)
              .map(([mint, _]) => mint);
            
                         if (tokensWithHoldings.length > 0) {
               // Run metadata fetching in parallel
               fetchTokenMetadata(currentViewingWallet, tokensWithHoldings).catch(err => {
                 console.error('Failed to fetch token metadata:', err);
               });
               
               // Subscribe to real-time price updates for new tokens
               subscribeToTokenPrices(tokensWithHoldings).catch(err => {
                 console.error('Failed to subscribe to token prices:', err);
               });
             }
            
          } catch (error) {
            console.error('❌ Error fetching PnL data:', error);
            setError(`Failed to load PnL data: ${error instanceof Error ? error.message : 'Unknown error'}`);
          } finally {
            setIsLoadingPnL(false);
            isLoadingRef.current = false;
            setIsSynchronizing(false);
          }
        }
      };
      
      fetchData();
    }
  }, [currentViewingWallet, isLoading]); // Keep only essential dependencies

  // Listen for active wallet changes
  useEffect(() => {
    const handleActiveWalletChanged = (event: CustomEvent<{previousWallet: string | null, currentWallet: string | null}>) => {
      const { currentWallet } = event.detail;
      console.log(`🔄 ActivePositions: Active wallet changed event received: ${currentWallet?.slice(0, 8) || 'null'}...`);
      
      setActiveWalletKey(currentWallet);
      
      // FIXED: Also update the viewing wallet to the newly activated trading wallet
      if (currentWallet) {
        console.log(`📊 ActivePositions: Switching view to newly activated trading wallet: ${currentWallet.slice(0, 8)}...`);
        setIsSynchronizing(true);
        setCurrentViewingWallet(currentWallet);
      } else {
        // If no active trading wallet, fall back to first available trading wallet
        console.log(`📊 ActivePositions: No active trading wallet, falling back to first trading wallet`);
        if (availableWallets.length > 0) {
          setIsSynchronizing(true);
          setCurrentViewingWallet(availableWallets[0].address);
        }
      }
    };
    
    window.addEventListener('ekko:active_wallet_changed', handleActiveWalletChanged as EventListener);
    
    return () => {
      window.removeEventListener('ekko:active_wallet_changed', handleActiveWalletChanged as EventListener);
    };
  }, [walletUser.walletAddress]);

  // Load quick sell preferences
  useEffect(() => {
    const loadQuickSellPreferences = async () => {
      if (!walletUser.walletAddress) return;
      
      try {
        const preferences = await getActivePositionsQuickSellPreferences(walletUser.walletAddress);
        setQuickSellPreferences(preferences);
        console.log('✅ Loaded quick sell preferences:', preferences);
      } catch (error) {
        console.error('Error loading quick sell preferences:', error);
      }
    };

    loadQuickSellPreferences();
  }, [walletUser.walletAddress]);

  // Cleanup WebSocket subscriptions on unmount
  useEffect(() => {
    return () => {
      console.log('🔌 ActivePositions: Component unmounting, cleaning up WebSocket subscriptions');
      unsubscribeFromAllTokens();
    };
  }, []); // Only run on mount/unmount

  // Background polling for P&L updates every 5 seconds
  useEffect(() => {
    if (!currentViewingWallet || isLoading) return;

    console.log(`⏰ Setting up background P&L polling for wallet ${currentViewingWallet.slice(0, 8)}... (every 5 seconds)`);
    
    const pollInterval = setInterval(async () => {
      if (!currentViewingWallet || isLoadingRef.current) return;
      
      try {
        console.log(`🔄 Background P&L refresh for wallet ${currentViewingWallet.slice(0, 8)}...`);
        
        // Temporarily set loading ref to prevent duplicate calls
        isLoadingRef.current = true;
        
        const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
        if (!apiKey) {
          console.warn('⚠️ Background refresh skipped: API key not found');
          return;
        }
        
        const response = await fetch(`https://data.solanatracker.io/pnl/${currentViewingWallet}`, {
          method: 'GET',
          headers: {
            'x-api-key': apiKey,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          console.warn(`⚠️ Background P&L refresh failed: ${response.status} ${response.statusText}`);
          return;
        }
        
        const pnlData = await response.json();
        
        // Silently update the walletPnL state
        setWalletPnL(prev => ({
          ...prev,
          [currentViewingWallet]: pnlData
        }));
        
        console.log(`✅ Background P&L refresh completed for wallet ${currentViewingWallet.slice(0, 8)}...`, {
          tokensCount: Object.keys(pnlData.tokens || {}).length,
          tokensWithHoldings: Object.entries(pnlData.tokens || {}).filter(([_, token]) => (token as any).holding > 0).length
        });
        
      } catch (error) {
        console.warn('⚠️ Background P&L refresh error:', error);
      } finally {
        isLoadingRef.current = false;
      }
    }, 5000); // Poll every 5 seconds

    // Cleanup interval on unmount or wallet change
    return () => {
      console.log(`⏰ Cleaning up background P&L polling for wallet ${currentViewingWallet.slice(0, 8)}...`);
      clearInterval(pollInterval);
    };
  }, [currentViewingWallet, isLoading]); // Re-setup polling when wallet changes

  // Handle quick sell preference updates
  const updateQuickSellPrefs = async (updates: Partial<ActivePositionsQuickSellPreferences>) => {
    if (!walletUser.walletAddress) return;

    try {
      const success = await updateActivePositionsQuickSellPreferences(walletUser.walletAddress, updates);
      if (success) {
        setQuickSellPreferences(prev => ({ ...prev, ...updates }));
        console.log('✅ Updated quick sell preferences:', updates);
      }
    } catch (error) {
      console.error('Error updating quick sell preferences:', error);
    }
  };

  // Handle token sell
  const handleTokenSell = async (token: TokenBalance) => {
    if (!walletUser.walletAddress || !currentViewingWallet || !quickSellPreferences.enabled) return;

    const tokenMint = token.mint;

    // If this is the first click, show the confirm button
    if (!confirmSells[tokenMint]) {
      setConfirmSells(prev => ({ ...prev, [tokenMint]: true }));
      
      // Auto-reset confirmation after 5 seconds
      setTimeout(() => {
        setConfirmSells(prev => {
          const newState = { ...prev };
          delete newState[tokenMint];
          return newState;
        });
      }, 5000);
      
      return;
    }

    // This is the confirmation click - proceed with sell
    try {
      setSellLoading(prev => ({ ...prev, [tokenMint]: true }));
      setConfirmSells(prev => {
        const newState = { ...prev };
        delete newState[tokenMint];
        return newState;
      });

      // Calculate amount to sell (percentage of balance)
      const totalBalance = parseFloat(token.balance);
      const amountToSell = (totalBalance * quickSellPreferences.sellPercentage) / 100;

      console.log(`💰 Selling ${quickSellPreferences.sellPercentage}% of ${token.symbol}: ${amountToSell} tokens`);

      // Use Jupiter service sellToken method
      const result = await jupiterService.sellToken({
        tokenMint,
        amount: amountToSell,
        slippage: quickSellPreferences.slippagePercent
      });

      if (result.success && result.txId) {
        console.log(`✅ Successfully sold ${amountToSell} ${token.symbol} tokens - TX: ${result.txId}`);
        
        // Show success notification with auto-dismiss
        window.dispatchEvent(new CustomEvent('ekko:show_toast', {
          detail: {
            message: `Successfully sold ${amountToSell} ${token.symbol || 'tokens'}!`,
            type: 'success',
            id: `sell-success-${tokenMint}-${Date.now()}`,
            tokenMint,
            duration: 3000 // 3 seconds
          }
        }));
        
        // Refresh token data after successful sale
        console.log(`🔄 Starting automatic refresh sequence after successful sale...`);
        
        // Wait 2 seconds before starting refresh sequence
        setTimeout(async () => {
          if (!currentViewingWallet) return;
          
          console.log(`🔄 Refresh 1/3: Fetching updated PnL data...`);
          try {
            await fetchWalletPnL(currentViewingWallet);
          } catch (error) {
            console.error('❌ Refresh 1/3 failed:', error);
          }
          
          // Second refresh after 2 more seconds
          setTimeout(async () => {
            if (!currentViewingWallet) return;
            
            console.log(`🔄 Refresh 2/3: Fetching updated PnL data...`);
            try {
              await fetchWalletPnL(currentViewingWallet);
            } catch (error) {
              console.error('❌ Refresh 2/3 failed:', error);
            }
            
            // Third refresh after 2 more seconds
            setTimeout(async () => {
              if (!currentViewingWallet) return;
              
              console.log(`🔄 Refresh 3/3: Fetching updated PnL data...`);
              try {
                await fetchWalletPnL(currentViewingWallet);
                console.log(`✅ Automatic refresh sequence completed after token sale`);
              } catch (error) {
                console.error('❌ Refresh 3/3 failed:', error);
              }
            }, 2000);
          }, 2000);
        }, 2000);
      }

    } catch (error) {
      console.error('❌ Error selling token:', error);
      
      // Show user-friendly error message
      let errorMessage = error instanceof Error ? error.message : 'Unknown error during sale';
      
      // Handle common error types
      if (errorMessage.includes('insufficient')) {
        errorMessage = 'Insufficient token balance for this sale amount';
      } else if (errorMessage.includes('slippage')) {
        errorMessage = 'Slippage tolerance exceeded. Try increasing slippage or smaller amount.';
      } else if (errorMessage.includes('Pool is completed')) {
        errorMessage = 'Token has completed bonding curve. Use a DEX like Raydium instead.';
      }
      
      console.error(`💥 Sale failed: ${errorMessage}`);
    } finally {
      setSellLoading(prev => {
        const newState = { ...prev };
        delete newState[tokenMint];
        return newState;
      });
    }
  };

  // Handle token click to update other widgets
  const handleTokenClick = (token: TokenBalance) => {
    // Create a token object that matches the Token interface expected by other widgets
    const tokenData: Token = {
      id: token.mint, // Use mint as ID since we don't have a separate ID
      mint: token.mint,
      symbol: token.symbol || null,
      name: token.name || null,
      signature: null,
      trader_public_key: null,
      tx_type: null,
      sol_amount: null,
      initial_buy: null,
      market_cap_sol: null,
      token_amount: null,
      bonding_curve_key: null,
      v_sol_in_bonding_curve: null,
      v_tokens_in_bonding_curve: null,
      uri: null,
      metadata: null,
      timestamp: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      description: null,
      image_uri: token.image || null,
      image_url: token.image || null,
      twitter: token.socials?.twitter || null,
      telegram: token.socials?.telegram || null,
      website: token.socials?.website || null,
      discord: token.socials?.discord || null,
      attributes: null,
      dev_projects: null,
      is_rugged: null,
      rug_date: null,
      rug_type: null,
      buys: null,
      sells: null,
      holders: null,
      volume: null,
      liquidity: null,
      token_price: token.value ? token.value / parseFloat(token.balance) : null,
      snipers: null,
      insiders: null,
      top_10_holders: null,
      bonding_curve_progress: null,
      sequence_number: null,
      price_usd: token.value ? token.value / parseFloat(token.balance) : null,
      liquidity_usd: null,
      market_cap_usd: null,
      volume_usd: null,
      tx_volume: null,
      buys_count: null,
      sells_count: null,
      holders_count: null,
      position: null,
      market_type: null,
      market: null,
      userBalance: token.balance,
      userBalanceUsd: token.value,
      youtube: null,
      is_raydium: undefined,
      is_migrated: undefined,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      volume_24h: null,
      jupiter: null,
      risk: undefined,
      events: undefined,
      pools: undefined,
      dev_wallet: null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      dev_sell_time: null
    };

    // Dispatch the token_selected event that AdvancedCharts listens for
    const event = new CustomEvent('ekko:token_selected', { 
      detail: { token: tokenData }
    });
    window.dispatchEvent(event);
    
    console.log(`🎯 Token selected from Active Positions: ${token.symbol} (${token.mint})`);
  };

  const refreshBalances = async () => {
    if (!walletUser.walletAddress || isSynchronizing) return;
    
    setIsRefreshing(true);
    console.log(`🔄 Manual refresh triggered for wallet ${currentViewingWallet?.slice(0, 8)}...`);
    
    try {
      // Refresh PnL data and sniper positions for current viewing wallet
      if (currentViewingWallet) {
        await Promise.all([
          fetchWalletPnL(currentViewingWallet),
          fetchSniperPositions(currentViewingWallet)
        ]);
      }
      console.log(`✅ Manual refresh completed successfully`);
    } catch (err) {
      console.error('❌ Error refreshing balances:', err);
      setError('Failed to refresh balances');
    } finally {
      setIsRefreshing(false);
    }
  };

  const activateWallet = async (walletPublicKey: string) => {
    if (!walletUser.walletAddress) return;
    
    try {
      const previousWallet = activeWalletKey;
      const newActiveKey = activeWalletKey === walletPublicKey ? null : walletPublicKey;
      
      // If we're activating a wallet, restore its keypair
      if (newActiveKey) {
        const keypair = await restoreTradingWalletKeypair(walletUser.walletAddress, newActiveKey);
        if (!keypair) {
          throw new Error('Failed to restore trading wallet keypair');
        }
      }
      
      // Update the active wallet in the database
      const success = await updateActiveTradingWallet(walletUser.walletAddress, newActiveKey);
      
      if (success) {
        setActiveWalletKey(newActiveKey);
        notifyActiveWalletChanged(previousWallet, newActiveKey);
      }
    } catch (err) {
      console.error('Error activating/deactivating trading wallet:', err);
      setError('Failed to update active wallet');
    }
  };

  const formatBalance = (balance: string) => {
    const value = parseFloat(balance);
    if (isNaN(value)) return '0';
    if (value === 0) return '0';
    if (value < 0.0001) return '<0.0001';
    if (value < 0.01) return value.toFixed(4);
    if (value < 1) return value.toFixed(3);
    if (value < 1000) return value.toFixed(2);
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '$0';
    if (Math.abs(value) >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(2)}M`;
    } else if (Math.abs(value) >= 1_000) {
      return `$${(value / 1_000).toFixed(2)}K`;
    } else if (Math.abs(value) < 0.01 && value !== 0) {
      return `$${value.toFixed(6)}`;
    } else {
      return `$${value.toFixed(2)}`;
    }
  };

  const formatPnL = (value: number) => {
    const formatted = formatCurrency(value);
    return value >= 0 ? `+${formatted}` : formatted;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(text);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  // Calculate real-time summary outside of render to avoid hooks in conditionals
  const realtimeSummary = React.useMemo(() => {
    if (!currentViewingWallet || !walletPnL[currentViewingWallet]) {
      return null;
    }
    return calculateRealTimeSummary(currentViewingWallet);
  }, [currentViewingWallet, walletPnL, realtimePrices, calculateRealTimeSummary]);

  // Sharing functions
  const handleShareToken = (tokenMint: string, tokenPnL: TokenPnL) => {
    const metadata = tokenMetadata[tokenMint];
    const realTimePnL = calculateRealTimePnL(tokenMint, tokenPnL);
    const roi = tokenPnL.total_invested > 0 ? (realTimePnL.total / tokenPnL.total_invested) * 100 : 0;
    
    const shareData: ShareData = {
      type: 'token',
      tokenMint,
      tokenSymbol: metadata?.symbol || tokenMint.slice(0, 6),
      tokenName: metadata?.name || 'Unknown Token',
      tokenImage: metadata?.image || null,
      pnl: realTimePnL.total,
      roi,
      invested: tokenPnL.total_invested,
      currentValue: realTimePnL.currentValue
    };
    
    // Clear previous card URL when switching tokens
    setGeneratedCardUrl(null);
    setShareData(shareData);
    setShareCustomization(prev => ({
      ...prev,
      customText: `Just ${realTimePnL.total >= 0 ? 'made' : 'lost'} ${formatPnL(realTimePnL.total)} on $${shareData.tokenSymbol}! 🚀`
    }));
    setShowShareModal(true);
  };

  const handleShareWallet = () => {
    if (!currentViewingWallet || !walletPnL[currentViewingWallet]) return;
    
    const summary = realtimeSummary || walletPnL[currentViewingWallet].summary;
    const tokens = Object.entries(walletPnL[currentViewingWallet].tokens);
    const tokensWithHoldings = tokens.filter(([_, tokenPnL]) => (tokenPnL as TokenPnL).holding > 0);
    
    const shareData: ShareData = {
      type: 'wallet',
      pnl: summary.total,
      invested: summary.totalInvested,
      currentValue: summary.totalInvested + summary.total,
      walletAddress: currentViewingWallet,
      walletName: availableWallets.find(w => w.address === currentViewingWallet)?.name || 'My Wallet',
      totalTokens: tokensWithHoldings.length,
      winRate: summary.winPercentage
    };
    
    // Clear previous card URL when switching to wallet share
    setGeneratedCardUrl(null);
    setShareData(shareData);
    setShareCustomization(prev => ({
      ...prev,
      customText: `Portfolio Update: ${summary.total >= 0 ? 'Up' : 'Down'} ${formatPnL(summary.total)} across ${tokensWithHoldings.length} tokens! 📊`
    }));
    setShowShareModal(true);
  };

  const generateShareCard = async () => {
    if (!shareData) return;
    
    setIsGeneratingCard(true);
    
    try {
      // Create canvas for card generation
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');
      
      // Set canvas size (landscape format)
      canvas.width = 1200;
      canvas.height = 630;
      
      // Load Roboto font first
      const robotoFont = new FontFace('Roboto', 'url(https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2)');
      await robotoFont.load();
      document.fonts.add(robotoFont);
      
      // Background
      if (shareCustomization.backgroundStyle === 'custom' && shareCustomization.customBackgroundImage) {
        // Custom background image at specified opacity
        const img = document.createElement('img');
        img.onload = () => {
          if (ctx) {
            ctx.globalAlpha = shareCustomization.tokenBackgroundOpacity / 100;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            ctx.globalAlpha = 1.0;
            drawCardContent();
          }
        };
        img.src = shareCustomization.customBackgroundImage;
      } else if (shareCustomization.backgroundStyle === 'token' && shareCustomization.useTokenAsBackground && shareData?.tokenImage) {
        // Token background image at specified opacity
        const tokenBgImg = document.createElement('img');
        tokenBgImg.crossOrigin = 'anonymous';
        tokenBgImg.onload = () => {
          if (ctx) {
            ctx.globalAlpha = shareCustomization.tokenBackgroundOpacity / 100;
            ctx.drawImage(tokenBgImg, 0, 0, canvas.width, canvas.height);
            ctx.globalAlpha = 1.0;
            drawCardContent();
          }
        };
        tokenBgImg.onerror = () => {
          // Fallback to gradient if token image fails
          drawGradientBackground();
          drawCardContent();
        };
        tokenBgImg.src = shareData.tokenImage;
      } else {
        // Default gradient background
        drawGradientBackground();
        drawCardContent();
      }
      
      function drawGradientBackground() {
        if (!ctx) return;
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        switch (shareCustomization.colorScheme) {
          case 'green':
            gradient.addColorStop(0, '#16161a');
            gradient.addColorStop(1, '#1a2e1a');
            break;
          case 'blue':
            gradient.addColorStop(0, '#16161a');
            gradient.addColorStop(1, '#1a1e2e');
            break;
          case 'purple':
            gradient.addColorStop(0, '#16161a');
            gradient.addColorStop(1, '#2e1a2e');
            break;
          case 'gold':
            gradient.addColorStop(0, '#16161a');
            gradient.addColorStop(1, '#2e2a1a');
            break;
        }
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      function drawCardContent() {
        // Main content with Roboto font
        if (!ctx || !shareData) return;
        
        // Load and draw EKKO logo in top right (bigger and moved in)
        const ekkoLogo = document.createElement('img');
        ekkoLogo.crossOrigin = 'anonymous';
        ekkoLogo.onload = () => {
          // Draw EKKO logo in top right (120x48 size, moved in more)
          if (!ctx) return;
          ctx.drawImage(ekkoLogo, canvas.width - 140, 15, 120, 48);
          drawMainContent();
        };
        ekkoLogo.onerror = () => {
          // Fallback if logo fails to load
          if (!ctx) return;
          ctx.fillStyle = '#24c35b';
          ctx.font = 'bold 28px Roboto, Arial, sans-serif';
          ctx.textAlign = 'right';
          ctx.fillText('EKKO', canvas.width - 25, 45);
          ctx.textAlign = 'left';
          drawMainContent();
        };
        ekkoLogo.src = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png';
        
        function drawMainContent() {
          // Load and draw Solana logo in bottom right (bigger and moved in)
          const solanaLogo = document.createElement('img');
          solanaLogo.crossOrigin = 'anonymous';
          solanaLogo.onload = async () => {
            // Draw Solana logo in bottom right (50x50 size, moved in more)
            if (!ctx) return;
            ctx.drawImage(solanaLogo, canvas.width - 70, canvas.height - 70, 50, 50);
            await drawContent();
          };
          solanaLogo.onerror = async () => {
            // Fallback Solana logo (bigger)
            if (!ctx) return;
            ctx.beginPath();
            ctx.arc(canvas.width - 45, canvas.height - 45, 25, 0, 2 * Math.PI);
            ctx.fillStyle = '#9945FF';
            ctx.fill();
            ctx.fillStyle = 'white';
            ctx.font = 'bold 14px Roboto, Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('SOL', canvas.width - 45, canvas.height - 40);
            ctx.textAlign = 'left';
            await drawContent();
          };
          solanaLogo.src = 'https://solana.com/src/img/branding/solanaLogoMark.svg';
        }
        
        async function drawContent() {
          // Main content with Roboto font and improved layout
          if (!ctx || !shareData) return;
          
          ctx.textAlign = 'left';
          ctx.fillStyle = 'white';
          
          if (shareData.type === 'token') {
            // Token share card with image support
            let contentStartX = 50;
            let tokenImageSize = 100; // Larger size for better visibility
            let currentY = 120; // Start below EKKO logo
            
            // Load and draw token image if available and enabled first
            if (shareData.tokenImage && shareCustomization.showTokenImage) {
              try {
                const tokenImg = document.createElement('img');
                tokenImg.crossOrigin = 'anonymous';
                
                let hasDrawn = false; // Prevent duplicate drawing
                
                const imageLoadPromise = new Promise<void>((resolve) => {
                  tokenImg.onload = () => {
                    if (!hasDrawn) {
                      hasDrawn = true;
                      drawTokenContent(true, tokenImg);
                    }
                    resolve();
                  };
                  tokenImg.onerror = () => {
                    if (!hasDrawn) {
                      hasDrawn = true;
                      drawTokenContent(false, null);
                    }
                    resolve();
                  };
                  tokenImg.src = shareData.tokenImage;
                });
                
                // Add timeout for image loading
                const timeoutPromise = new Promise<void>((resolve) => {
                  setTimeout(() => {
                    if (!hasDrawn) {
                      hasDrawn = true;
                      drawTokenContent(false, null);
                    }
                    resolve();
                  }, 2000);
                });
                
                // Wait for either image load or timeout
                await Promise.race([imageLoadPromise, timeoutPromise]);
              } catch (error) {
                console.warn('Token image loading failed:', error);
                drawTokenContent(false, null);
              }
            } else {
              drawTokenContent(false, null);
            }
            
            function drawTokenContent(hasImage: boolean, tokenImg: HTMLImageElement | null) {
              if (!ctx || !shareData) return;
              
              // Token symbol at the top left
              ctx.fillStyle = 'white';
              ctx.font = 'bold 48px Roboto, Arial, sans-serif';
              ctx.fillText(`$${shareData.tokenSymbol}`, contentStartX, currentY);
              
              // Draw token image next to the symbol if available
              if (hasImage && tokenImg) {
                try {
                  // Measure text width to position image next to it
                  const symbolWidth = ctx.measureText(`$${shareData.tokenSymbol}`).width;
                  
                  // Draw circular token image next to the symbol with aspect ratio preservation
                  const imageX = contentStartX + symbolWidth + 30; // 30px gap after symbol for larger image
                  const imageY = currentY - tokenImageSize + 5; // Better alignment with larger image
                  
                  ctx.save();
                  ctx.beginPath();
                  ctx.arc(imageX + tokenImageSize/2, imageY + tokenImageSize/2, tokenImageSize/2, 0, 2 * Math.PI);
                  ctx.clip();
                  
                  // Calculate aspect ratio and draw image to fill circle while maintaining ratio (like object-fit: cover)
                  const imgAspectRatio = tokenImg.naturalWidth / tokenImg.naturalHeight;
                  const circleAspectRatio = 1; // Circle is always 1:1
                  
                  let drawWidth, drawHeight, drawX, drawY;
                  
                  if (imgAspectRatio > circleAspectRatio) {
                    // Image is wider - fit by height and crop sides
                    drawHeight = tokenImageSize;
                    drawWidth = tokenImageSize * imgAspectRatio;
                    drawX = imageX - (drawWidth - tokenImageSize) / 2;
                    drawY = imageY;
                  } else {
                    // Image is taller or square - fit by width and crop top/bottom
                    drawWidth = tokenImageSize;
                    drawHeight = tokenImageSize / imgAspectRatio;
                    drawX = imageX;
                    drawY = imageY - (drawHeight - tokenImageSize) / 2;
                  }
                  
                  ctx.drawImage(tokenImg, drawX, drawY, drawWidth, drawHeight);
                  ctx.restore();
                  
                  // Draw border around token image
                  ctx.beginPath();
                  ctx.arc(imageX + tokenImageSize/2, imageY + tokenImageSize/2, tokenImageSize/2, 0, 2 * Math.PI);
                  ctx.strokeStyle = '#24c35b';
                  ctx.lineWidth = 3;
                  ctx.stroke();
                } catch (error) {
                  console.warn('Error drawing token image:', error);
                }
              }
              
              currentY += 50;
              
              // Token name below symbol
              ctx.font = 'bold 24px Roboto, Arial, sans-serif';
              ctx.fillStyle = '#888';
              ctx.fillText(shareData.tokenName || '', contentStartX, currentY);
              currentY += 80; // More space before P&L
              
              // PnL (main stat) - centered vertically
              ctx.font = 'bold 64px Roboto, Arial, sans-serif';
              ctx.fillStyle = shareData.pnl >= 0 ? '#24c35b' : '#ef4444';
              const pnlText = shareCustomization.currencyDisplay === 'SOL' 
                ? `${shareData.pnl >= 0 ? '+' : ''}${(shareData.pnl / 200).toFixed(4)} SOL`
                : formatPnL(shareData.pnl);
              ctx.fillText(pnlText, contentStartX, currentY);
              currentY += 80;
              
              if (shareCustomization.showROI && shareData.roi !== undefined) {
                ctx.font = 'bold 36px Roboto, Arial, sans-serif';
                ctx.fillStyle = shareData.roi >= 0 ? '#24c35b' : '#ef4444';
                ctx.fillText(`${shareData.roi >= 0 ? '+' : ''}${shareData.roi.toFixed(2)}% ROI`, contentStartX, currentY);
                currentY += 50;
              }
              
              if (shareCustomization.showInvested && shareData.invested) {
                ctx.font = 'bold 28px Roboto, Arial, sans-serif';
                ctx.fillStyle = '#aaa';
                const investedText = shareCustomization.currencyDisplay === 'SOL'
                  ? `Invested: ${(shareData.invested / 200).toFixed(4)} SOL`
                  : `Invested: ${formatCurrency(shareData.invested)}`;
                ctx.fillText(investedText, contentStartX, currentY);
                currentY += 40;
              }
              
              if (shareCustomization.showCurrentValue && shareData.currentValue) {
                ctx.font = 'bold 28px Roboto, Arial, sans-serif';
                ctx.fillStyle = '#aaa';
                const currentValueText = shareCustomization.currencyDisplay === 'SOL'
                  ? `Current: ${(shareData.currentValue / 200).toFixed(4)} SOL`
                  : `Current: ${formatCurrency(shareData.currentValue)}`;
                ctx.fillText(currentValueText, contentStartX, currentY);
                currentY += 40;
              }
              
              // Continue with custom text and timestamp
              drawCommonContent();
            }
            
          } else {
            // Wallet share card
            drawWalletContent();
          }
          
          function drawWalletContent() {
            if (!ctx || !shareData) return;
            
            let currentY = 120; // Start below EKKO branding
            
            ctx.font = 'bold 48px Roboto, Arial, sans-serif';
            ctx.fillStyle = 'white';
            ctx.fillText('Portfolio Performance', 50, currentY);
            currentY += 80;
            
            // Total PnL
            ctx.font = 'bold 64px Roboto, Arial, sans-serif';
            ctx.fillStyle = shareData.pnl >= 0 ? '#24c35b' : '#ef4444';
            const walletPnlText = shareCustomization.currencyDisplay === 'SOL'
              ? `${shareData.pnl >= 0 ? '+' : ''}${(shareData.pnl / 200).toFixed(4)} SOL`
              : formatPnL(shareData.pnl);
            ctx.fillText(walletPnlText, 50, currentY);
            currentY += 80;
            
            if (shareCustomization.showInvested && shareData.invested) {
              ctx.font = 'bold 24px Roboto, Arial, sans-serif';
              ctx.fillStyle = '#888';
              const totalInvestedText = shareCustomization.currencyDisplay === 'SOL'
                ? `Total Invested: ${(shareData.invested / 200).toFixed(4)} SOL`
                : `Total Invested: ${formatCurrency(shareData.invested)}`;
              ctx.fillText(totalInvestedText, 50, currentY);
              currentY += 40;
            }
            
            if (shareCustomization.showWinRate && shareData.winRate !== undefined) {
              ctx.font = 'bold 24px Roboto, Arial, sans-serif';
              ctx.fillStyle = '#888';
              ctx.fillText(`Win Rate: ${shareData.winRate.toFixed(0)}%`, 50, currentY);
              currentY += 40;
            }
            
            if (shareCustomization.showTokenCount && shareData.totalTokens) {
              ctx.font = 'bold 24px Roboto, Arial, sans-serif';
              ctx.fillStyle = '#888';
              ctx.fillText(`${shareData.totalTokens} Active Positions`, 50, currentY);
              currentY += 40;
            }
            
            // Wallet addresses
            if (shareCustomization.showPrimaryWallet && walletUser.walletAddress) {
              ctx.font = 'bold 20px Roboto, monospace';
              ctx.fillStyle = '#888';
              ctx.fillText(`Primary: ${walletUser.walletAddress.slice(0, 8)}...${walletUser.walletAddress.slice(-8)}`, 50, currentY);
              currentY += 35;
            }
            
            if (shareCustomization.showTradingWallet && shareData.walletAddress) {
              ctx.font = 'bold 20px Roboto, monospace';
              ctx.fillStyle = '#888';
              ctx.fillText(`Trading: ${shareData.walletAddress.slice(0, 8)}...${shareData.walletAddress.slice(-8)}`, 50, currentY);
              currentY += 35;
            }
            
            drawCommonContent();
          }
          
          function drawCommonContent() {
            if (!ctx || !shareData) return;
            
            // Custom text - positioned to avoid overlap
            if (shareCustomization.customText) {
              ctx.font = 'bold 32px Roboto, Arial, sans-serif';
              ctx.fillStyle = 'white';
              ctx.fillText(shareCustomization.customText, 50, canvas.height - 100);
            }
            
            // Timestamp - bottom left
            ctx.font = 'bold 22px Roboto, Arial, sans-serif';
            ctx.fillStyle = '#666';
            ctx.fillText(new Date().toLocaleDateString(), 50, canvas.height - 40);
            
            // Solana logo - bottom right
            try {
              const solanaImg = new Image();
              solanaImg.crossOrigin = 'anonymous';
              
              const solanaPromise = new Promise<void>((resolve) => {
                solanaImg.onload = () => {
                  try {
                    // Draw Solana logo in bottom right
                    const logoSize = 40;
                    const logoX = canvas.width - logoSize - 20;
                    const logoY = canvas.height - logoSize - 20;
                    ctx.drawImage(solanaImg, logoX, logoY, logoSize, logoSize);
                  } catch (error) {
                    console.warn('Error drawing Solana logo:', error);
                  }
                  resolve();
                };
                solanaImg.onerror = () => {
                  // Fallback Solana logo
                  try {
                    const logoSize = 40;
                    const logoX = canvas.width - logoSize - 20;
                    const logoY = canvas.height - logoSize - 20;
                    
                    ctx.beginPath();
                    ctx.arc(logoX + logoSize/2, logoY + logoSize/2, logoSize/2, 0, 2 * Math.PI);
                    ctx.fillStyle = '#9945FF';
                    ctx.fill();
                    ctx.fillStyle = 'white';
                    ctx.font = 'bold 14px Roboto, Arial, sans-serif';
                    ctx.textAlign = 'center';
                    ctx.fillText('SOL', logoX + logoSize/2, logoY + logoSize/2 + 4);
                    ctx.textAlign = 'left';
                  } catch (error) {
                    console.warn('Error drawing fallback Solana logo:', error);
                  }
                  resolve();
                };
                solanaImg.src = 'https://cryptologos.cc/logos/solana-sol-logo.png';
              });
              
              // Add timeout for logo loading
              const logoTimeout = new Promise<void>((resolve) => {
                setTimeout(() => resolve(), 1000);
              });
              
              Promise.race([solanaPromise, logoTimeout]).then(() => {
                // Convert to blob and create URL after everything is drawn
                canvas.toBlob((blob) => {
                  if (blob) {
                    const url = URL.createObjectURL(blob);
                    setGeneratedCardUrl(url);
                  }
                }, 'image/png');
              });
            } catch (error) {
              console.warn('Solana logo loading failed:', error);
              // Still generate the card without the logo
              canvas.toBlob((blob) => {
                if (blob) {
                  const url = URL.createObjectURL(blob);
                  setGeneratedCardUrl(url);
                }
              }, 'image/png');
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error generating share card:', error);
    } finally {
      setIsGeneratingCard(false);
    }
  };

  const downloadCard = () => {
    if (!generatedCardUrl || !shareData) return;
    
    const link = document.createElement('a');
    link.href = generatedCardUrl;
    link.download = `ekko-${shareData.type}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareToTwitter = () => {
    if (!shareData) return;
    
    const text = shareCustomization.customText || 
      (shareData.type === 'token' 
        ? `Just ${shareData.pnl >= 0 ? 'made' : 'lost'} ${formatPnL(shareData.pnl)} on $${shareData.tokenSymbol}!`
        : `Portfolio Update: ${shareData.pnl >= 0 ? 'Up' : 'Down'} ${formatPnL(shareData.pnl)}!`);
    
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text + ' 🚀\n\nPowered by $EKKO @EKKOPlatform')}&url=${encodeURIComponent('https://ekko-platform.com')}`;
    window.open(url, '_blank');
  };

  // Auto-generate card when share data or customization changes
  useEffect(() => {
    if (shareData && showShareModal && !isGeneratingCard) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        generateShareCard();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [shareData, shareCustomization, showShareModal]);

  // Clear generated card URL when modal is closed
  useEffect(() => {
    if (!showShareModal) {
      setGeneratedCardUrl(null);
    }
  }, [showShareModal]);

  // Hidden tokens management functions
  const loadHiddenTokens = useCallback(() => {
    try {
      const stored = localStorage.getItem('ekko-hidden-tokens');
      if (stored) {
        const hiddenArray = JSON.parse(stored);
        setHiddenTokens(new Set(hiddenArray));
      }
    } catch (error) {
      console.error('Error loading hidden tokens:', error);
    }
  }, []);

  const saveHiddenTokens = useCallback((tokens: Set<string>) => {
    try {
      localStorage.setItem('ekko-hidden-tokens', JSON.stringify(Array.from(tokens)));
    } catch (error) {
      console.error('Error saving hidden tokens:', error);
    }
  }, []);

  const hideToken = useCallback((tokenMint: string) => {
    setHiddenTokens(prev => {
      const newHidden = new Set(prev);
      newHidden.add(tokenMint);
      saveHiddenTokens(newHidden);
      return newHidden;
    });
  }, [saveHiddenTokens]);

  const unhideToken = useCallback((tokenMint: string) => {
    setHiddenTokens(prev => {
      const newHidden = new Set(prev);
      newHidden.delete(tokenMint);
      saveHiddenTokens(newHidden);
      return newHidden;
    });
  }, [saveHiddenTokens]);

  // Load hidden tokens on component mount
  useEffect(() => {
    loadHiddenTokens();
  }, [loadHiddenTokens]);

  return (
    <div className="flex flex-col h-full bg-white text-gray-900">
      {/* Custom CSS for slider styling */}
      <style>{`
        /* Range slider styling */
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #24c35b;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .slider::-webkit-slider-thumb:hover {
          background: #22b55a;
          transform: scale(1.1);
        }
        
        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #24c35b;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .slider::-moz-range-thumb:hover {
          background: #22b55a;
          transform: scale(1.1);
        }
      `}</style>
      {/* Header */}
      <div className="p-2 border-b border-[#24c35b] bg-gray-50">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Wallet size={16} className="text-[#24c35b]" />
            <h3 className="text-sm font-medium text-gray-900">Active Positions</h3>
            
            {/* Wallet Selector Dropdown */}
            <GlobalDropdown
              options={availableWallets.map(wallet => ({
                value: wallet.address,
                label: wallet.name,
                subtitle: `${wallet.address.slice(0, 8)}...${wallet.address.slice(-4)}`,
                badge: wallet.type === 'main' ? 'MAIN' : (wallet.isActive ? 'ACTIVE' : undefined),
                badgeColor: wallet.type === 'main' ? 'main' : 'active',
                balance: `${(wallet.balance || 0).toFixed(3)} SOL`,
                usdValue: wallet.balance && wallet.balance > 0 ? `$${(wallet.balance * 200).toFixed(0)}` : '$0'
              }))}
              value={currentViewingWallet || ''}
              onChange={(address) => {
                console.log(`🔄 User selected wallet: ${address.slice(0, 8)}...`);
                setIsSynchronizing(true);
                setCurrentViewingWallet(address);
              }}
              placeholder="Select Wallet"
              className="max-w-[140px]"
            />
            
            {/* Token Count Display */}
            {currentViewingWallet && (
              <div className="flex items-center gap-2 ml-2">
                <span className="text-xs text-gray-400">
                  {walletPnL[currentViewingWallet] ? Object.entries(walletPnL[currentViewingWallet].tokens).filter(([_, tokenPnL]) => tokenPnL.holding > 0).length : 0} tokens
                </span>
                {isLoadingPnL || isLoadingMetadata ? (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 flex items-center gap-1">
                    <Loader size={10} className="animate-spin" />
                    {isLoadingPnL ? 'LOADING PnL' : 'LOADING TOKENS'}
                  </span>
                ) : walletPnL[currentViewingWallet] ? (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">
                    🟢 LOADED
                  </span>
                ) : (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-400">
                    🔴 NO DATA
                  </span>
                )}
                
                {/* WebSocket Status */}
                {activeSubscriptions.size > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-[#24c35b]/20 text-[#24c35b] flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-[#24c35b] rounded-full animate-pulse"></span>
                    LIVE ({activeSubscriptions.size})
                  </span>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={refreshBalances}
              className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
              disabled={isRefreshing || isSynchronizing}
            >
              <RefreshCw size={14} className={`text-[#24c35b] ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            {onClose && (
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-gray-700/20"
                title="Close"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-x-auto custom-scrollbar bg-white">
        {/* Tabs for Visible/Hidden tokens */}
        <div className="p-2 border-b border-gray-200/50 bg-gray-50">
          <div className="flex items-center gap-2">
                          <button
                onClick={() => setShowingHidden(false)}
                className={`text-xs px-3 py-1.5 rounded ${
                  !showingHidden 
                    ? 'bg-[#24c35b]/20 text-[#24c35b]' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Visible
              </button>
              <button
                onClick={() => setShowingHidden(true)}
                className={`text-xs px-3 py-1.5 rounded ${
                  showingHidden 
                    ? 'bg-[#24c35b]/20 text-[#24c35b]' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Hidden ({hiddenTokens.size})
              </button>
          </div>
        </div>

        {/* P&L Summary Section */}
        <div className="p-2 border-b border-gray-200/50 bg-gray-50">
          <div className="grid grid-cols-4 gap-2 mb-2">
            <div className="p-2 rounded-lg bg-white border border-[#24c35b]/20">
              <div className="text-xs text-gray-600 mb-1">Total P&L</div>
              <div className="text-sm font-semibold">
                {currentViewingWallet && walletPnL[currentViewingWallet] ? (() => {
                  const total = realtimeSummary?.total ?? walletPnL[currentViewingWallet].summary.total;
                  return (
                    <span className={total >= 0 ? 'text-[#24c35b]' : 'text-red-500'}>
                      {formatPnL(total)}
                    </span>
                  );
                })() : (
                  <span className="text-gray-500">-</span>
                )}
              </div>
            </div>
            
            <div className="p-2 rounded-lg bg-white border border-[#24c35b]/20">
              <div className="text-xs text-gray-600 mb-1">Invested</div>
              <div className="text-sm font-semibold text-gray-900">
                {currentViewingWallet && walletPnL[currentViewingWallet] ? (
                  formatCurrency(walletPnL[currentViewingWallet].summary.totalInvested)
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </div>
            </div>
            
            <div className="p-2 rounded-lg bg-white border border-[#24c35b]/20">
              <div className="text-xs text-gray-600 mb-1">Realized</div>
              <div className="text-sm font-semibold">
                {currentViewingWallet && walletPnL[currentViewingWallet] ? (
                  <span className={walletPnL[currentViewingWallet].summary.realized >= 0 ? 'text-[#24c35b]' : 'text-red-500'}>
                    {formatPnL(walletPnL[currentViewingWallet].summary.realized)}
                  </span>
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </div>
            </div>
            
            <div className="p-2 rounded-lg bg-white border border-[#24c35b]/20">
              <div className="text-xs text-gray-600 mb-1">Unrealized</div>
              <div className="text-sm font-semibold">
                {currentViewingWallet && walletPnL[currentViewingWallet] ? (() => {
                  const unrealized = realtimeSummary?.unrealized ?? walletPnL[currentViewingWallet].summary.unrealized;
                  return (
                    <span className={unrealized >= 0 ? 'text-[#24c35b]' : 'text-red-500'}>
                      {formatPnL(unrealized)}
                    </span>
                  );
                })() : (
                  <span className="text-gray-500">-</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Performance Stats */}
          <div className="flex justify-between items-center text-xs">
            <div className="flex items-center">
              <TrendingUp size={12} className="text-[#24c35b] mr-1" />
              <span className="text-gray-900">Performance</span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center">
                <span className={`mr-1 ${currentViewingWallet && walletPnL[currentViewingWallet] ? 'text-[#24c35b]' : 'text-gray-500'}`}>
                  {currentViewingWallet && walletPnL[currentViewingWallet] ? 
                    `${walletPnL[currentViewingWallet].summary.winPercentage.toFixed(0)}%` : 
                    '-%'
                  }
                </span>
                <span className="text-gray-400">Win</span>
              </div>
              
              <div className="flex items-center">
                <span className={`mr-1 ${currentViewingWallet && walletPnL[currentViewingWallet] ? 'text-red-500' : 'text-gray-500'}`}>
                  {currentViewingWallet && walletPnL[currentViewingWallet] ? 
                    `${walletPnL[currentViewingWallet].summary.lossPercentage.toFixed(0)}%` : 
                    '-%'
                  }
                </span>
                <span className="text-gray-400">Loss</span>
              </div>
              
              <div className="flex items-center">
                <span className={`mr-1 ${currentViewingWallet && walletPnL[currentViewingWallet] ? 'text-white' : 'text-gray-500'}`}>
                  {currentViewingWallet && walletPnL[currentViewingWallet] ? 
                    `${walletPnL[currentViewingWallet].summary.totalWins}/${walletPnL[currentViewingWallet].summary.totalWins + walletPnL[currentViewingWallet].summary.totalLosses}` : 
                    '-/-'
                  }
                </span>
                <span className="text-gray-400">W/L</span>
              </div>
              
              {/* Wallet Share Button */}
              {currentViewingWallet && walletPnL[currentViewingWallet] && (
                <button
                  onClick={handleShareWallet}
                  className="p-1.5 rounded-lg bg-[#24c35b]/10 hover:bg-[#24c35b]/20 border border-[#24c35b]/20 hover:border-[#24c35b]/40 transition-all group"
                  title="Share wallet performance"
                >
                  <Share2 size={12} className="text-[#24c35b] group-hover:text-white" />
                </button>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader size={20} className="animate-spin text-[#24c35b]" />
          </div>
        ) : isSynchronizing ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-2">
              <span className="text-gray-900 text-sm">Synchronising active wallet positions</span>
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-[#24c35b] rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-[#24c35b] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-[#24c35b] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="text-red-500 text-sm text-center">{error}</div>
        ) : !currentViewingWallet ? (
          <div className="text-gray-600 text-sm text-center">No wallet selected for viewing</div>
        ) : (
          <div className="flex flex-col bg-white">
            {currentViewingWallet && walletPnL[currentViewingWallet] ? (
              (() => {
                const tokens = Object.entries(walletPnL[currentViewingWallet].tokens);
                
                // Filter based on hidden/visible status first
                let filteredTokens = showingHidden
                  ? tokens.filter(([tokenMint]) => hiddenTokens.has(tokenMint))
                  : tokens.filter(([tokenMint]) => !hiddenTokens.has(tokenMint));
                
                // Then filter by holdings (only for visible tokens or if we're showing hidden tokens)
                const tokensWithHoldings = showingHidden 
                  ? filteredTokens // Show all hidden tokens regardless of holdings
                  : filteredTokens.filter(([_, tokenPnL]) => tokenPnL.holding > 0);
                
                // Removed console.log to prevent spam during re-renders
                return tokensWithHoldings
                  .sort((a, b) => {
                    // Sort by total P&L (highest profits first, then smallest losses)
                    return b[1].total - a[1].total;
                  });
              })()
                .map(([tokenMint, tokenPnL]) => {
                  // Calculate real-time P&L values
                  const realTimePnL = calculateRealTimePnL(tokenMint, tokenPnL);
                  const hasRealtimeData = realtimePrices[tokenMint] !== undefined;
                  
                  return (
                    <div
                      key={tokenMint}
                      className="flex items-center px-2 py-1.5 border-b border-gray-200/50 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => {
                        const metadata = tokenMetadata[tokenMint];
                        handleTokenClick({
                          mint: tokenMint,
                          symbol: metadata?.symbol || tokenMint.slice(0, 6),
                          name: metadata?.name || tokenMint.slice(0, 8),
                          balance: tokenPnL.holding.toString(),
                          value: realTimePnL.currentValue,
                          image: metadata?.image || null
                        });
                      }}
                    >
                      {/* Token Info */}
                      <div className="flex items-center gap-1.5 w-[130px] min-w-[130px]">
                        {(() => {
                          const metadata = tokenMetadata[tokenMint];
                          return metadata?.image ? (
                            <img 
                              src={metadata.image} 
                              alt={metadata.symbol || 'Token'} 
                              className="w-4 h-4 rounded-full"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                ((e.target as HTMLImageElement).nextElementSibling as HTMLElement)!.style.display = 'flex';
                              }}
                            />
                          ) : null;
                        })()}
                        <div className="w-4 h-4 rounded-full bg-[#24c35b]/10 flex items-center justify-center" style={{display: tokenMetadata[tokenMint]?.image ? 'none' : 'flex'}}>
                          <span className="text-[9px] text-[#24c35b]">
                            {(tokenMetadata[tokenMint]?.symbol || tokenMint).substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1">
                            <div className="text-xs font-medium text-gray-900 truncate">
                              {tokenMetadata[tokenMint]?.symbol || tokenMint.slice(0, 6)}
                            </div>
                            {hasRealtimeData && (
                              <span className="w-1 h-1 bg-[#24c35b] rounded-full animate-pulse" title="Real-time price"></span>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(tokenMint);
                              }}
                              className="p-0.5 hover:bg-white/5 rounded transition-colors flex-shrink-0"
                              title="Copy token address"
                            >
                              {copiedAddress === tokenMint ? (
                                <Check size={10} className="text-[#24c35b]" />
                              ) : (
                                <Copy size={10} className="text-gray-400" />
                              )}
                            </button>
                          </div>
                          <div className="text-[9px] text-gray-400 truncate">
                            {tokenMetadata[tokenMint]?.name || tokenMint}
                          </div>
                        </div>
                      </div>

                      <div className="h-4 w-px bg-white/10 mx-1" />

                      {/* Balance */}
                      <div className="w-[90px] min-w-[90px]">
                        <div className="text-[10px] text-gray-400">Balance</div>
                        <div className="text-xs font-bold text-gray-900">
                          {formatBalance(tokenPnL.holding.toString())}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {formatCurrency(realTimePnL.currentValue)}
                        </div>
                      </div>

                      <div className="h-4 w-px bg-white/10 mx-1" />

                      {/* P&L */}
                      <div className="w-[100px] min-w-[100px]">
                        <div className="text-[10px] text-gray-400">Total P&L</div>
                        <div className={`text-xs font-bold ${
                          realTimePnL.total >= 0 ? 'text-[#24c35b]' : 'text-[#ef4444]'
                        }`}>
                          {formatPnL(realTimePnL.total)}
                        </div>
                        <div className="text-xs text-gray-400">
                        Current: {formatCurrency(realTimePnL.currentValue)}
                        </div>
                      </div>

                      <div className="h-4 w-px bg-white/10 mx-1" />

                      {/* ROI */}
                      <div className="w-[90px] min-w-[90px]">
                        <div className="text-xs text-gray-400">ROI</div>
                        {(() => {
                          const roi = tokenPnL.total_invested > 0 
                            ? (realTimePnL.total / tokenPnL.total_invested) * 100 
                            : 0;
                          return (
                            <div className={`text-sm font-bold flex items-center ${
                              roi >= 0 ? 'text-[#24c35b]' : 'text-[#ef4444]'
                            }`}>
                              {roi >= 0 ? (
                                <TrendingUp size={12} className="mr-1" />
                              ) : (
                                <TrendingDown size={12} className="mr-1" />
                              )}
                              {roi >= 0 ? '+' : ''}{roi.toFixed(2)}%
                            </div>
                          );
                        })()}
                      </div>

                      <div className="h-4 w-px bg-white/10 mx-1" />

                      {/* Unrealized */}
                      <div className="w-[120px] min-w-[120px]">
                        <div className="text-xs text-gray-400">Unrealized</div>
                        <div className={`text-sm font-bold ${
                          realTimePnL.unrealized >= 0 ? 'text-[#24c35b]' : 'text-[#ef4444]'
                        }`}>
                          {formatPnL(realTimePnL.unrealized)}
                        </div>
                        <div className="text-xs text-gray-400">
                        Invested: {formatCurrency(tokenPnL.total_invested)}
                        </div>
                      </div>

                      <div className="h-4 w-px bg-white/10 mx-1" />

                      {/* Quick Sell Button */}
                      <div className="w-[80px] min-w-[80px] flex items-center justify-center">
                        {quickSellPreferences.enabled && (
                          <button
                            onClick={(e) => {
                                e.stopPropagation();
                                const metadata = tokenMetadata[tokenMint];
                                handleTokenSell({
                                  mint: tokenMint,
                                  symbol: metadata?.symbol || tokenMint.slice(0, 6),
                                  name: metadata?.name || tokenMint.slice(0, 8),
                                  balance: tokenPnL.holding.toString(),
                                  value: realTimePnL.currentValue,
                                  image: metadata?.image || null
                                });
                              }}
                              disabled={sellLoading[tokenMint]}
                            className={`px-3 py-1 rounded text-xs font-medium transition-all duration-200 ${
                                confirmSells[tokenMint]
                                ? 'bg-[#ef4444] hover:bg-[#dc2626] text-white shadow-lg'
                                : 'bg-[#24c35b] hover:bg-[#22b55a] text-black'
                            } ${
                                sellLoading[tokenMint] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            title={
                                confirmSells[tokenMint]
                                  ? `Confirm sell ${quickSellPreferences.sellPercentage}% (${(tokenPnL.holding * quickSellPreferences.sellPercentage / 100).toFixed(4)} tokens)`
                                : `Sell ${quickSellPreferences.sellPercentage}%`
                            }
                          >
                              {sellLoading[tokenMint] ? (
                              <div className="flex items-center gap-1">
                                <Loader size={12} className="animate-spin" />
                                <span>Selling</span>
                              </div>
                              ) : confirmSells[tokenMint] ? (
                              <div className="flex items-center gap-1">
                                <Check size={12} />
                                <span>Confirm</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <DollarSign size={12} />
                                <span>Sell</span>
                              </div>
                            )}
                          </button>
                        )}
                      </div>

                      <div className="h-4 w-px bg-white/10 mx-1" />

                      {/* Hide/Unhide Button */}
                      <div className="w-[60px] min-w-[60px] flex items-center justify-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (showingHidden) {
                              unhideToken(tokenMint);
                            } else {
                              hideToken(tokenMint);
                            }
                          }}
                          className={`p-1.5 rounded-lg border transition-all group ${
                            showingHidden
                              ? 'bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20 hover:border-blue-500/40'
                              : 'bg-gray-500/10 hover:bg-gray-500/20 border-gray-500/20 hover:border-gray-500/40'
                          }`}
                          title={showingHidden ? 'Unhide token' : 'Hide token'}
                        >
                          {showingHidden ? (
                            <Eye size={12} className="text-blue-400 group-hover:text-white" />
                          ) : (
                            <EyeOff size={12} className="text-gray-400 group-hover:text-white" />
                          )}
                        </button>
                      </div>

                      <div className="h-4 w-px bg-white/10 mx-1" />

                      {/* Token Share Button */}
                      <div className="w-[60px] min-w-[60px] flex items-center justify-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShareToken(tokenMint, tokenPnL);
                          }}
                          className="p-1.5 rounded-lg bg-[#24c35b]/10 hover:bg-[#24c35b]/20 border border-[#24c35b]/20 hover:border-[#24c35b]/40 transition-all group"
                          title="Share token performance"
                        >
                          <Share2 size={12} className="text-[#24c35b] group-hover:text-white" />
                        </button>
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="p-8 text-center text-gray-400">
                {isLoadingPnL ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader size={20} className="animate-spin text-[#24c35b]" />
                    <span>Loading token data...</span>
                </div>
                ) : (
                  <>
                    <div className="text-lg mb-2">📭 No Active Positions</div>
                    <div className="text-sm">No tokens with current holdings found</div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick Sell Preferences Section */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <DollarSign size={14} className={quickSellPreferences.enabled ? "text-[#24c35b]" : "text-gray-500"} />
            <h4 className="text-sm font-medium text-black">Quick Sell Settings</h4>
            {!quickSellPreferences.enabled && (
              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">DISABLED</span>
            )}
          </div>
          <button
            onClick={() => setShowQuickSellSettings(!showQuickSellSettings)}
            className="p-1 rounded hover:bg-gray-200 transition-colors"
          >
            <Settings size={14} className="text-gray-600" />
          </button>
        </div>
        
        {showQuickSellSettings ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-black text-xs font-medium mb-1 block">Sell Percentage</label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {[1, 5, 10, 20, 50, 75, 100].map(percent => (
                    <button
                      key={percent}
                      onClick={() => updateQuickSellPrefs({ sellPercentage: percent })}
                      className={`p-1.5 rounded-lg border text-xs font-medium transition-all ${
                        quickSellPreferences.sellPercentage === percent
                          ? 'border-[#24c35b] bg-[#24c35b] text-white'
                          : 'border-gray-300 bg-white text-gray-600 hover:border-[#24c35b] hover:text-black'
                        }`}
                    >
                      {percent}%
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={quickSellPreferences.sellPercentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      const numValue = parseInt(value) || 1;
                      if (numValue >= 1 && numValue <= 100) {
                        updateQuickSellPrefs({ sellPercentage: numValue });
                      } else if (value === '') {
                        updateQuickSellPrefs({ sellPercentage: 1 });
                      }
                    }
                  }}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b]/20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="Custom %"
                  inputMode="numeric"
                />
              </div>
              
              <div>
                <label className="text-black text-xs font-medium mb-1 block">Slippage</label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {[10, 20, 30, 50].map(slippage => (
                    <button
                      key={slippage}
                      onClick={() => updateQuickSellPrefs({ slippagePercent: slippage })}
                      className={`p-1.5 rounded-lg border text-xs font-medium transition-all ${
                        quickSellPreferences.slippagePercent === slippage
                          ? 'border-[#24c35b] bg-[#24c35b] text-white'
                          : 'border-gray-300 bg-white text-gray-600 hover:border-[#24c35b] hover:text-black'
                        }`}
                    >
                      {slippage}%
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={quickSellPreferences.slippagePercent}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      const numValue = parseFloat(value) || 0.1;
                      if (numValue >= 0.1 && numValue <= 50) {
                        updateQuickSellPrefs({ slippagePercent: numValue });
                      } else if (value === '') {
                        updateQuickSellPrefs({ slippagePercent: 0.1 });
                      }
                    }
                  }}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b]/20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="Custom %"
                  inputMode="decimal"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-black text-xs font-medium">Enable Quick Sell</span>
              <button
                onClick={() => updateQuickSellPrefs({ enabled: !quickSellPreferences.enabled })}
                className={`w-10 h-5 rounded-full transition-colors ${
                  quickSellPreferences.enabled ? 'bg-[#24c35b]' : 'bg-gray-300'
                }`}
              >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                  quickSellPreferences.enabled ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {quickSellPreferences.enabled ? (
                <>Quick sell <span className="text-[#24c35b] font-bold">{quickSellPreferences.sellPercentage}%</span> with {quickSellPreferences.slippagePercent}% slippage</>
              ) : (
                <span className="text-gray-500">Quick sell is disabled</span>
              )}
            </div>
            {!quickSellPreferences.enabled && (
              <button
                onClick={() => updateQuickSellPrefs({ enabled: true })}
                className="text-xs bg-[#24c35b] text-white px-3 py-1.5 rounded-lg hover:bg-[#20a855] transition-colors shadow-sm"
              >
                Enable
              </button>
            )}
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && shareData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]" onClick={() => setShowShareModal(false)}>
          <div className="bg-white border border-gray-200 rounded-2xl w-[1400px] max-w-[98vw] max-h-[95vh] overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
                <Share2 size={20} className="text-[#24c35b]" />
                <h2 className="text-lg font-semibold text-black">
                  Share {shareData?.type === 'token' ? `$${shareData?.tokenSymbol || ''}` : 'Portfolio'} Performance
                </h2>
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="flex h-[700px]">
              {/* Left Panel - Customization Options */}
              <div className="w-1/3 p-4 border-r border-gray-200 overflow-y-auto custom-scrollbar">
                <h3 className="text-lg font-semibold text-black mb-4">Customize Your Card</h3>
                
                <div className="space-y-6">
                  {/* Custom Text */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-black block">Custom Message</label>
                    <textarea
                      value={shareCustomization.customText}
                      onChange={(e) => setShareCustomization(prev => ({ ...prev, customText: e.target.value }))}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b]/20 resize-none"
                      rows={3}
                      placeholder="Add your custom message..."
                    />
                  </div>

                  {/* Background Image Upload */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-black block">Background Image</label>
                    
                    {/* Global Background Upload */}
                    <div className="space-y-2">
                      <label className="text-xs text-black block">Custom Background</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setShareCustomization(prev => ({ 
                                  ...prev, 
                                  customBackgroundImage: event.target?.result as string,
                                  backgroundStyle: 'custom',
                                  useTokenAsBackground: false
                                }));
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                          id="background-upload"
                        />
                        <label
                          htmlFor="background-upload"
                          className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-black text-sm cursor-pointer hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                        >
                          <Upload size={14} />
                          Choose Background
                        </label>
                        {shareCustomization.customBackgroundImage && (
                          <button
                            onClick={() => setShareCustomization(prev => ({ 
                              ...prev, 
                              customBackgroundImage: null,
                              backgroundStyle: 'gradient'
                            }))}
                            className="px-2 py-2 bg-red-100 border border-red-300 rounded-lg text-red-600 hover:bg-red-200 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      {shareCustomization.customBackgroundImage && (
                        <div className="mt-2">
                          <img
                            src={shareCustomization.customBackgroundImage}
                            alt="Background preview"
                            className="w-full h-20 object-cover rounded-lg border border-gray-300"
                          />
                        </div>
                      )}
                    </div>

                    {/* Token Background Toggle - only show for token shares */}
                    {shareData?.type === 'token' && shareData?.tokenImage && (
                      <div className="space-y-2">
                        <label className="flex items-center justify-between">
                          <span className="text-xs text-black">Use token as background</span>
                          <button
                            onClick={() => setShareCustomization(prev => ({ 
                              ...prev, 
                              useTokenAsBackground: !prev.useTokenAsBackground,
                              backgroundStyle: !prev.useTokenAsBackground ? 'token' : 'gradient',
                              customBackgroundImage: !prev.useTokenAsBackground ? null : prev.customBackgroundImage
                            }))}
                            className={`w-10 h-5 rounded-full transition-colors ${
                              shareCustomization.useTokenAsBackground ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              shareCustomization.useTokenAsBackground ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </button>
                        </label>
                        {shareCustomization.useTokenAsBackground && (
                          <div className="mt-2">
                            <img
                              src={shareData.tokenImage}
                              alt="Token background preview"
                              className="w-full h-20 object-cover rounded-lg border border-gray-600/30"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Background Opacity Slider */}
                    {(shareCustomization.useTokenAsBackground || shareCustomization.customBackgroundImage) && (
                      <div className="space-y-2">
                        <label className="text-xs text-black block">
                          Background Opacity: {shareCustomization.tokenBackgroundOpacity}%
                        </label>
                        <input
                          type="range"
                          min="5"
                          max="80"
                          value={shareCustomization.tokenBackgroundOpacity}
                          onChange={(e) => setShareCustomization(prev => ({ 
                            ...prev, 
                            tokenBackgroundOpacity: parseInt(e.target.value) 
                          }))}
                          className="w-full h-2 bg-gray-600/30 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>5%</span>
                          <span>80%</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Color Scheme & Currency */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-black mb-2 block">Color Scheme</label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { key: 'green', name: 'Green', color: '#24c35b' },
                            { key: 'blue', name: 'Blue', color: '#3b82f6' },
                            { key: 'purple', name: 'Purple', color: '#8b5cf6' },
                            { key: 'gold', name: 'Gold', color: '#f59e0b' }
                          ].map(scheme => (
                            <button
                              key={scheme.key}
                              onClick={() => setShareCustomization(prev => ({ ...prev, colorScheme: scheme.key as any }))}
                              className={`px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                shareCustomization.colorScheme === scheme.key
                                  ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                                  : 'border-gray-600/30 bg-gray-600/10 text-gray-400 hover:border-[#24c35b]/40 hover:text-gray-300'
                              }`}
                            >
                              <div className="w-3 h-3 rounded-full mx-auto mb-1" style={{ backgroundColor: scheme.color }}></div>
                              {scheme.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-black mb-2 block">Currency</label>
                        <div className="grid grid-cols-1 gap-2">
                          <button
                            onClick={() => setShareCustomization(prev => ({ ...prev, currencyDisplay: 'SOL' }))}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                              shareCustomization.currencyDisplay === 'SOL'
                                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                                : 'border-gray-600/30 bg-gray-600/10 text-gray-400 hover:border-[#24c35b]/40 hover:text-gray-300'
                            }`}
                          >
                            SOL
                          </button>
                          <button
                            onClick={() => setShareCustomization(prev => ({ ...prev, currencyDisplay: 'USD' }))}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                              shareCustomization.currencyDisplay === 'USD'
                                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                                : 'border-gray-600/30 bg-gray-600/10 text-gray-400 hover:border-[#24c35b]/40 hover:text-gray-300'
                            }`}
                          >
                            USD
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Display Options */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-black block">Display Options</label>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between">
                        <span className="text-sm text-black">Show exact amounts</span>
                        <button
                          onClick={() => setShareCustomization(prev => ({ ...prev, showExactAmounts: !prev.showExactAmounts }))}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            shareCustomization.showExactAmounts ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            shareCustomization.showExactAmounts ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </label>

                      <label className="flex items-center justify-between">
                        <span className="text-sm text-black">Show ROI</span>
                        <button
                          onClick={() => setShareCustomization(prev => ({ ...prev, showROI: !prev.showROI }))}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            shareCustomization.showROI ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            shareCustomization.showROI ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </label>

                      <label className="flex items-center justify-between">
                        <span className="text-sm text-black">Show invested</span>
                        <button
                          onClick={() => setShareCustomization(prev => ({ ...prev, showInvested: !prev.showInvested }))}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            shareCustomization.showInvested ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            shareCustomization.showInvested ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </label>

                      <label className="flex items-center justify-between">
                        <span className="text-sm text-black">Show current value</span>
                        <button
                          onClick={() => setShareCustomization(prev => ({ ...prev, showCurrentValue: !prev.showCurrentValue }))}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            shareCustomization.showCurrentValue ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            shareCustomization.showCurrentValue ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </label>

                      {/* Token Image Toggle - only show for token shares */}
                      {shareData?.type === 'token' && shareData?.tokenImage && (
                        <label className="flex items-center justify-between">
                          <span className="text-sm text-black">Show token image</span>
                          <button
                            onClick={() => setShareCustomization(prev => ({ ...prev, showTokenImage: !prev.showTokenImage }))}
                            className={`w-10 h-5 rounded-full transition-colors ${
                              shareCustomization.showTokenImage ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              shareCustomization.showTokenImage ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </button>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Wallet-specific Options */}
                  {shareData?.type === 'wallet' && (
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-black block">Wallet Options</label>
                      <div className="space-y-3">
                        <label className="flex items-center justify-between">
                          <span className="text-sm text-black">Show token count</span>
                          <button
                            onClick={() => setShareCustomization(prev => ({ ...prev, showTokenCount: !prev.showTokenCount }))}
                            className={`w-10 h-5 rounded-full transition-colors ${
                              shareCustomization.showTokenCount ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              shareCustomization.showTokenCount ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </button>
                        </label>

                        <label className="flex items-center justify-between">
                          <span className="text-sm text-black">Show win rate</span>
                          <button
                            onClick={() => setShareCustomization(prev => ({ ...prev, showWinRate: !prev.showWinRate }))}
                            className={`w-10 h-5 rounded-full transition-colors ${
                              shareCustomization.showWinRate ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              shareCustomization.showWinRate ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </button>
                        </label>

                        <label className="flex items-center justify-between">
                          <span className="text-sm text-black">Show wallets</span>
                          <button
                            onClick={() => setShareCustomization(prev => ({ ...prev, showPrimaryWallet: !prev.showPrimaryWallet, showTradingWallet: !prev.showTradingWallet }))}
                            className={`w-10 h-5 rounded-full transition-colors ${
                              shareCustomization.showPrimaryWallet || shareCustomization.showTradingWallet ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              shareCustomization.showPrimaryWallet || shareCustomization.showTradingWallet ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </button>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel - Preview and Actions */}
              <div className="w-2/3 p-4 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">Preview</h3>
                  {isGeneratingCard && (
                    <div className="flex items-center gap-2 text-sm text-[#24c35b]">
                      <Loader size={16} className="animate-spin" />
                      <span>Generating...</span>
                    </div>
                  )}
                </div>
                
                <div className="flex-1 flex items-center justify-center bg-gray-900/20 rounded-lg border border-gray-600/20 mb-4 min-h-[500px]">
                  {generatedCardUrl ? (
                    <img
                      src={generatedCardUrl}
                      alt="Share card preview"
                      className="max-w-full max-h-full object-contain rounded shadow-lg"
                    />
                  ) : (
                    <div className="text-center text-gray-400">
                      <div className="text-xl mb-3">📸 Preview</div>
                      <div className="text-sm mb-4">Generate card to see preview</div>
                      <div className="text-xs text-gray-500">
                        Customize your settings and click "Generate Card"
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  {generatedCardUrl ? (
                    <>
                      <button
                        onClick={downloadCard}
                        className="flex-1 bg-gray-600/20 hover:bg-gray-600/30 text-white py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 border border-gray-600/30"
                      >
                        <Download size={18} />
                        Download
                      </button>
                      
                      <button
                        onClick={shareToTwitter}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <XLogo size={18} />
                        Tweet
                      </button>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center py-3 px-6 text-gray-400">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-[#24c35b] border-t-transparent rounded-full animate-spin"></div>
                        <span>Generating card...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivePositions;

// Factory function to create the widget
export const createActivePositionsWidget = () => ({
  type: 'active-positions-widget',
  title: 'Active Positions',
  content: <ActivePositions />,
  size: { width: 750, height: 500 },
  contentStyle: { 
    padding: 0,
    overflow: "hidden",
  },
  glassMorphism: true,
      containerStyle: {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(12px)",
      borderRadius: "12px",
      border: "1px solid rgba(36, 195, 91, 0.2)",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)",
      overflow: "hidden",
    }
}); 